import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LocalstorageService } from '../../@services/localstorage.service';
import { NotificationService } from '../../@services/notification.service';
import { catchError, finalize, timeout } from 'rxjs/operators';
import { of } from 'rxjs';

export interface VoucherDialogData {
  totalAmount: number;
  merchantId: number;
  orderItems: { name: string; price: number; quantity: number }[];
}

export interface VoucherDialogResult {
  voucher: any;
  voucherAmount: number;
  remainingAmount: number;
  freeItemName?: string;
}

interface VoucherSearchResult {
  id: number;
  code: string;
  name: string;
  voucherType: string;
  discountValue: number;
  maxDiscountAmount: number;
  freeItemMode: string;
  freeItemName: string;
  minimumSpend: number;
  usageRemaining: number;
  startDate: string;
  endDate: string;
  calculatedDiscount: number;
  eligible: boolean;
  ineligibleReason: string;
}

@Component({
  selector: 'app-voucher-dialog',
  templateUrl: './voucher-dialog.component.html',
  styleUrls: ['./voucher-dialog.component.scss'],
})
export class VoucherDialogComponent {
  title = 'Apply Voucher';
  isMobile: boolean = false;

  searchTerm: string = '';
  searchResults: VoucherSearchResult[] = [];
  selectedVoucher: VoucherSearchResult | null = null;
  isSearching: boolean = false;
  hasSearched: boolean = false;

  voucherAmount: number = 0;
  remainingAmount: number = 0;
  freeItemName: string = '';

  constructor(
    private dialogRef: MatDialogRef<VoucherDialogComponent>,
    private http: HttpClient,
    private ns: NotificationService,
    private ls: LocalstorageService,
    @Inject(MAT_DIALOG_DATA) public data: VoucherDialogData
  ) {
    this.checkIsMobile();
    this.remainingAmount = this.data.totalAmount;
  }

  private checkIsMobile() {
    this.isMobile = window.innerWidth < 768;
  }

  search(event?) {
    if (event) event.preventDefault();
    this.doSearch(this.searchTerm?.trim() || '');
  }

  showAll() {
    this.doSearch('');
  }

  private doSearch(term: string) {
    this.isSearching = true;
    this.selectedVoucher = null;
    this.voucherAmount = 0;
    this.remainingAmount = this.data.totalAmount;
    this.searchResults = [];
    this.hasSearched = true;

    const payload = {
      searchTerm: term,
      merchantId: this.data.merchantId,
      orderTotal: this.data.totalAmount,
      orderItems: this.data.orderItems || [],
    };

    this.http
      .post<VoucherSearchResult[]>(`${environment.apiUrl}/api/voucher/searchVoucher`, payload)
      .pipe(
        timeout(10000),
        catchError((error) => {
          console.error('Voucher search error:', error);
          if (error.name === 'TimeoutError') {
            this.ns.showNotification({ message: 'Search timed out. Please try again.' });
          } else {
            this.ns.showNotification({ message: 'Error searching vouchers.' });
          }
          return of([]);
        }),
        finalize(() => {
          this.isSearching = false;
        })
      )
      .subscribe((results: VoucherSearchResult[]) => {
        if (!results || results.length === 0) {
          this.ns.showNotification({ message: 'No vouchers found.' });
          return;
        }
        // Sort: eligible first, then ineligible
        this.searchResults = results.sort((a, b) => {
          if (a.eligible === b.eligible) return 0;
          return a.eligible ? -1 : 1;
        });
      });
  }

  selectVoucher(v: VoucherSearchResult) {
    if (!v.eligible) {
      this.ns.showNotification({ message: v.ineligibleReason || 'This voucher is not eligible.' });
      return;
    }
    this.selectedVoucher = v;
    const safeTotalAmount = Math.max(0, this.data.totalAmount || 0);
    const calculatedDiscount = Math.max(0, v.calculatedDiscount || 0);
    this.voucherAmount = Math.min(calculatedDiscount, safeTotalAmount);
    this.remainingAmount = Math.max(0, safeTotalAmount - this.voucherAmount);
    this.freeItemName = v.voucherType === 'FREE_ITEM' ? 'Cheapest Item' : (v.freeItemName || '');
  }

  clearSelection() {
    this.selectedVoucher = null;
    this.voucherAmount = 0;
    this.remainingAmount = this.data.totalAmount;
    this.freeItemName = '';
  }

  getTypeLabel(v: VoucherSearchResult): string {
    switch (v.voucherType) {
      case 'FIXED_AMOUNT':
        return 'RM ' + (v.discountValue?.toFixed(2) || '0.00') + ' OFF';
      case 'PERCENTAGE':
        return v.discountValue + '% OFF' + (v.maxDiscountAmount ? ' (max RM ' + v.maxDiscountAmount.toFixed(2) + ')' : '');
      case 'FREE_ITEM':
        return 'FREE: Cheapest Item';
      default:
        return v.voucherType;
    }
  }

  submit() {
    if (!this.selectedVoucher || this.voucherAmount <= 0) {
      this.ns.showNotification({ message: 'Please select a valid voucher.' });
      return;
    }

    const result: VoucherDialogResult = {
      voucher: {
        id: this.selectedVoucher.id,
        code: this.selectedVoucher.code,
        name: this.selectedVoucher.name,
      },
      voucherAmount: this.voucherAmount,
      remainingAmount: this.remainingAmount,
      freeItemName: this.freeItemName || undefined,
    };

    this.dialogRef.close(result);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
